// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {SelectEl} from 'common/components/select';
import {InputEl} from 'common/components/input';
import {SvgIconEl} from 'common/components/svg-icon';
import {DropMenuEl} from 'common/components/drop-menu';
import {ItemsMenuEl} from 'common/components/items-menu';
import {SpinnerEl} from 'common/components/spinner';
import {RadioButtonEl} from 'common/components/radio-button';
import {DateRangePickerEl} from 'common/widgets/date-range-picker';
import {ButtonBarEl} from 'common/components/button-bar';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';

import type {PropertyValuesScreen} from './index';

export function template(this: PropertyValuesScreen) {
  const {editingPropertyType, propertyFilter, searchFilter} = this.state;
  const {t} = this.getContext(`i18n`);

  const FilterOperatorDropdown = () => (
    <SelectEl
      sel='.pvs-filter-operator-dropdown'
      attrs={{
        options: this.helpers.getFilterOperators(),
        'selected-option': propertyFilter.filterOperator,
      }}
      on={{
        change: (ev) => this.helpers.changeFilterOperator(ev.detail.value),
      }}
    />
  );

  const FilterNumberInput = ({
    value = ``,
    onInput = null,
    autofocus = false,
    key = null,
  }: {
    value: string | number;
    onInput: TODO;
    autofocus: boolean;
    key: string;
  }) => (
    <InputEl
      sel='.pvs-filter-numdate-input'
      attrs={{
        type: `number`,
      }}
      props={{
        value: value?.toString(),
      }}
      on={{
        input: onInput,
      }}
      hook={{
        insert: autofocus ? this.helpers.focusInput : null,
      }}
      key={key}
    />
  );

  const FilterStringInput = () => (
    <InputEl
      sel='.pvs-filter-string-input'
      attrs={{
        elref: `filterValueInput`,
        'select-on-focus': true,
        type: `text`,
        icon: `search`,
        placeholder: t(`filters.menu.search-placeholder`),
      }}
      props={{
        value: propertyFilter.filterValue?.toString(),
      }}
      on={{
        input: (ev) => this.helpers.changeFilterValue(ev.target.value),
      }}
      key={`string-${propertyFilter.filterOperator}`}
      hook={{
        insert: this.helpers.focusInput,
      }}
    />
  );

  return (
    <div
      sel='.pvs-container'
      class={{
        'pvs-display-context-custom-event':
          this.helpers.getDisplayContext() === this.helpers.PropertyFilterDisplayContext.CustomEvent,
      }}
      hook={{postpatch: this.helpers.dispatchDraw}}
    >
      {!this.attr(`display-value-only`) ? (
        <div sel='.pvs-title-section'>
          {this.helpers.hasBackButton() ? (
            <div
              sel='.pvs-back-button'
              on={{
                click: this.helpers.clickBackButton,
              }}
            >
              <SvgIconEl attrs={{icon: `caret-left`}} />
            </div>
          ) : null}
          <div sel='.pvs-property-type-container'>
            <div
              sel='.pvs-property-type-button'
              class={{'pvs-property-type-button-enabled': this.helpers.canTypeCast()}}
              on={{
                click: this.helpers.clickPropertyTypeButton,
              }}
            >
              {(() => {
                const propertyLabel = this.helpers.getPropertyLabel();

                return (
                  <>
                    <SvgIconEl attrs={{icon: this.helpers.getPropertyTypeIcon()}} />
                    <span
                      sel='.pvs-property-type-label'
                      attrs={{
                        title: propertyLabel,
                      }}
                    >
                      {this.helpers.truncateMiddle(propertyLabel, 30)}
                    </span>
                    {this.helpers.canTypeCast() ? <SvgIconEl attrs={{icon: `caret-down`}} /> : null}
                  </>
                );
              })()}
            </div>
            {this.helpers.canTypeCast() ? (
              <div sel='.pvs-property-types-menu'>
                <DropMenuEl
                  attrs={{open: editingPropertyType}}
                  on={{
                    change: this.helpers.changePropertyTypesMenu,
                  }}
                >
                  <ItemsMenuEl
                    props={{
                      sections: this.helpers.getPropertyTypeMenuSections(),
                    }}
                    on={{
                      clickItem: this.helpers.clickPropertyTypesMenuItem,
                    }}
                  />
                </DropMenuEl>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <div sel='.pvs-filter-section'>
        {(() => {
          const {propertyType, propertyObjectKey} = propertyFilter;

          return (
            <>
              {this.helpers.enhancedListFilterEnabled() ? (
                <div sel='.pvs-list-options'>
                  <div sel='.pvs-option-label'>Match</div>
                  <SelectEl
                    sel='.pvs-filter-list-quantifier'
                    props={{
                      sections: this.helpers.getListQuantifierSections(),
                    }}
                    on={{
                      change: this.helpers.handleListQuantifierSelect,
                    }}
                  />
                </div>
              ) : null}
              {propertyType === this.helpers.PropertyType.Object ? (
                this.helpers.isLoadingPropertyValues() ? (
                  <div elref='loadingPropertiesSpinner' sel='.pvs-filter-property-values-loading'>
                    <SpinnerEl />
                  </div>
                ) : (
                  <>
                    <div sel='.pvs-filter-fixed-subsection.pvs-filter-for-object.pvs-first-subsection'>
                      <div sel='.pvs-filter-label'>Where</div>
                      <SelectEl
                        attrs={{
                          'selected-option': propertyObjectKey === null ? `Choose a key` : ``,
                          tabindex: 0,
                        }}
                        props={{
                          sections: this.helpers.getPropertyValueSections(),
                        }}
                        on={{change: this.helpers.selectObjectPropertyKey}}
                      />
                    </div>
                    <div sel='.pvs-filter-fixed-subsection.pvs-filter-for-object'>
                      <div sel='.pvs-filter-label'>Equals</div>
                      <SelectEl
                        attrs={{
                          disabled: propertyObjectKey === null,
                          'selected-option': propertyObjectKey === null ? `Select a key first` : ``,
                          tabindex: 0,
                        }}
                        on={{change: this.helpers.selectObjectPropertyValue}}
                        props={{
                          sections: this.helpers.getObjectPropertyValueSections(),
                          sectionsFromInputFn: this.helpers.sectionsFromInputFn,
                        }}
                      />
                    </div>
                  </>
                )
              ) : propertyType === this.helpers.PropertyType.Number ? (
                <>
                  {!this.attr(`display-value-only`) ? (
                    <div sel='.pvs-filter-fixed-subsection.pvs-first-subsection'>
                      <FilterOperatorDropdown />
                    </div>
                  ) : null}
                  <div
                    sel='.pvs-filter-fixed-subsection'
                    class={{
                      'pvs-filter-numdate-between':
                        propertyFilter.filterOperator === this.helpers.NumberOperator.Between,
                    }}
                  >
                    {propertyFilter.filterOperator === this.helpers.NumberOperator.Between ||
                    propertyFilter.filterOperator === this.helpers.NumberOperator.NotBetween ? (
                      (() => {
                        const [minNum, maxNum] = propertyFilter.filterValue as [number, number];

                        return (
                          <>
                            <FilterNumberInput
                              value={minNum}
                              onInput={(ev) =>
                                this.helpers.changeFilterValue([this.helpers.parseNumber(ev.target.value), maxNum])
                              }
                              autofocus
                              key='num-min'
                            />
                            <div sel='.pvs-filter-conjunction'>and</div>
                            <FilterNumberInput
                              value={maxNum}
                              onInput={(ev) =>
                                this.helpers.changeFilterValue([minNum, this.helpers.parseNumber(ev.target.value)])
                              }
                              autofocus={false}
                              key='num-max'
                            />
                          </>
                        );
                      })()
                    ) : (
                      <FilterNumberInput
                        value={propertyFilter.filterValue as number}
                        onInput={(ev) => this.helpers.changeFilterValue(this.helpers.parseNumber(ev.target.value))}
                        autofocus
                        key={`num-val-${propertyFilter.filterOperator}`}
                      />
                    )}
                  </div>
                </>
              ) : propertyType === this.helpers.PropertyType.Boolean ||
                propertyType === this.helpers.PropertyType.List ? (
                <>
                  {!this.attr(`display-value-only`) ? (
                    <div sel='.pvs-filter-fixed-subsection.pvs-filter-radio-list.pvs-first-subsection'>
                      {/*TODO: For the custom events property filter, we only support 'contains' operator.*/}
                      {/*Use a different UI rather than a single radio button in this case?*/}
                      {this.helpers.getFilterOperators().map((filterOperator) => (
                        <div sel='.pvs-radio-button-container'>
                          <RadioButtonEl
                            className='pvs-radio-button'
                            selected={filterOperator === propertyFilter.filterOperator}
                            onClick={() => this.helpers.changeFilterOperator(filterOperator)}
                            children={filterOperator}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {propertyType === this.helpers.PropertyType.List ? (
                    <div sel='.pvs-filter-fixed-subsection'>
                      <FilterStringInput />
                    </div>
                  ) : null}
                </>
              ) : propertyType === this.helpers.PropertyType.String ? (
                <>
                  {!this.attr(`display-value-only`) ? (
                    <div sel='.pvs-filter-fixed-subsection.pvs-first-subsection'>
                      <FilterOperatorDropdown />
                    </div>
                  ) : null}
                  {propertyFilter.filterOperator === this.helpers.StringOperator.Contains ||
                  propertyFilter.filterOperator === this.helpers.StringOperator.DoesNotContain ? (
                    <>
                      <div sel='.pvs-filter-fixed-subsection'>
                        <FilterStringInput />
                      </div>
                      {this.helpers.isLoadingPropertyValues() ? (
                        <div sel='.pvs-filter-property-values-loading'>
                          <SpinnerEl
                            attrs={{
                              elref: `loadingPropertiesSpinner`,
                            }}
                          />
                        </div>
                      ) : (
                        <div sel='.pvs-filter-property-values'>
                          <ItemsMenuEl
                            sel='.pvs-property-value-list'
                            attrs={this.helpers.filterAttributes({
                              'search-exact': true,
                              'search-filter': propertyFilter.filterValue
                                ? this.helpers.renamePropertyValue(propertyFilter.filterValue)
                                : null,
                            })}
                            on={{
                              clickItem: (ev) => this.helpers.selectContainsPropertyValue(ev.detail.item.propertyValue),
                              draw: this.helpers.propagateEvent,
                            }}
                            props={{
                              sections: this.helpers.getPropertyValueSections(),
                              keydownProxyEl: this.helpers.getKeydownProxyEl(),
                            }}
                          />
                        </div>
                      )}
                    </>
                  ) : propertyFilter.filterOperator === this.helpers.StringOperator.Equals ||
                    propertyFilter.filterOperator === this.helpers.StringOperator.DoesNotEqual ? (
                    <>
                      <div sel='.pvs-filter-fixed-subsection'>
                        <InputEl
                          attrs={{
                            elref: `filterValueInput`,
                            icon: `search`,
                            placeholder: t(`filters.menu.search-placeholder`),
                          }}
                          props={{value: searchFilter}}
                          key={`string-${propertyFilter.filterOperator}`}
                          hook={{insert: this.helpers.insertedIconInput}}
                          on={{input: (ev) => this.helpers.changeSearchFilter(ev.target.value)}}
                        />
                      </div>
                      {this.helpers.isLoadingPropertyValues() && !searchFilter ? (
                        <div sel='.pvs-filter-property-values-loading'>
                          <SpinnerEl
                            attrs={{
                              elref: `loadingPropertiesSpinner`,
                            }}
                          />
                        </div>
                      ) : (
                        <div sel='.pvs-filter-property-values'>
                          <ItemsMenuEl
                            attrs={this.helpers.filterAttributes({
                              'search-filter': searchFilter,
                              'show-csv-matches': true,
                              'show-select-all': !this.attr(`disable-multi-select`),
                              'show-select-all-matching': !this.attr(`disable-multi-select`),
                              multiple: !this.attr(`disable-multi-select`),
                              elref: `string-property-select-menu`,
                            })}
                            on={{
                              selectAllMatching: this.helpers.selectAllMatching,
                              selectAll: this.helpers.selectAllPropertyValues,
                              clickItem: (ev) => this.helpers.toggleFilterItem(ev.detail.item),
                              draw: this.helpers.propagateEvent,
                            }}
                            props={{
                              sectionsFromInputFn: this.helpers.sectionsFromInputFn,
                              sections: this.helpers.getPropertyValueSections(),
                              keydownProxyEl: this.helpers.getKeydownProxyEl(),
                            }}
                          />
                        </div>
                      )}
                    </>
                  ) : null}
                </>
              ) : propertyType === this.helpers.PropertyType.Datetime ? (
                <DateRangePickerEl
                  attrs={this.helpers.filterAttributes({
                    'display-context': this.helpers.getDateRangePickerDisplayContext(),
                    'date-range': (propertyFilter.filterValue as DateRange).getAttributes(),
                  })}
                  on={{
                    changeDateRange: this.helpers.changeDateRange,
                    draw: this.helpers.propagateEvent,
                  }}
                />
              ) : null}
            </>
          );
        })()}
      </div>
      <ButtonBarEl
        sel='.mp-button-bar-primary'
        attrs={{
          disabled: !this.attr(`allow-invalid`) && !propertyFilter.isValid(),
          tabindex: 0,
        }}
        on={{
          click: this.helpers.clickAddFilter,
        }}
      >
        <slot attrs={{name: `apply`}}>{t(`common.add`)}</slot>
      </ButtonBarEl>
    </div>
  );
}
